import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, catchError, combineLatest, finalize, map, mergeMap, tap, throwError } from "rxjs";
import { LoginResponse, UserInfo, UserInfoResponse } from "./interface/auth.interface";
import { headers } from "src/app/general/helpers/headers.helper";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: 'root'
})

export class AuthService {
  public isLoggedIn: Observable<boolean | unknown>;
  url = environment.APP_ENDPOINT;
  _token: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  _user: BehaviorSubject<UserInfo | null> = new BehaviorSubject<UserInfo | null>(null);


  constructor(private http: HttpClient) {
    this.isLoggedIn = combineLatest([this.token]).pipe(map(([token]) => token));
  }

  init(): void {
    const token = localStorage.getItem('token');
    const rawUser = localStorage.getItem('user');

    if (token && rawUser) {
      this.setToken(token);
      this.setUser(JSON.parse(rawUser));
      return
    }

    this.clearAuthState();
  }

  /**
   * The token is stored before the profile is fetched, so a failure in the second
   * half of this chain would otherwise leave a token behind with no user next to it.
   * That state reads as logged in to AuthGuard and LoggedGuard while
   * NonInstallerGuard throws on the missing user, so any failure here has to
   * unwind the whole thing before the error reaches the caller.
   */
  login(body: {password: string, email: string}): Observable<UserInfoResponse> {
    return this.http.post<LoginResponse>(this.url + 'login', body).pipe(
      tap((res: LoginResponse) => {
        this.setUser(null);
        this.setToken(res.token);
      }),
      mergeMap(() => this.getUserInfo()),
      catchError((err) => {
        this.clearAuthState();
        return throwError(() => err);
      })
    )
  }

  getUserInfo(): Observable<UserInfoResponse> {
    return this.http.get<UserInfoResponse>(this.url + 'user/get-info', {headers: headers()}).pipe(
      tap((res: UserInfoResponse) => this.setUser(res.user))
    )
  }

  setToken(token: string | null): void {
    token ? localStorage.setItem('token', token) : localStorage.removeItem('token');
    this._token.next(token);
  }

  setUser(user: UserInfo | null): void {
    user ? localStorage.setItem('user', JSON.stringify(user)) : localStorage.removeItem('user');
    this._user.next(user);
  }

  clearAuthState(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('username');
    this._token.next(null);
    this._user.next(null);
  }

  get token(): Observable<string | null>{
    return this._token.asObservable();
  }

  get user(): Observable<UserInfo | null>{
    return this._user.asObservable();
  }

  logout(): Observable<{error?: string, message: string}> {
    return this.http.post<{error?: string, message: string}>(this.url + 'logout', {token: localStorage.getItem('token')}).pipe(
      finalize(() => this.clearAuthState())
    );
  }
}
